import React, { useState, useEffect } from 'react';
import { X, Mail, Clock, CheckCircle, XCircle, AlertCircle, Trash2, User, Copy, RefreshCw, Search } from 'lucide-react';
import { getInvitations, revokeInvitation, resendInvitation } from '../../lib/api';
import { toast } from '../../lib/toast';
import { useUser } from '../../store/useAuthStore';
import { useEscapeKey } from '../../hooks/useKeyboardShortcuts';
import { formatDate as formatDateUtil } from '../../utils/dateUtils';

interface InvitedByUser {
  _id: string;
  email: string;
  role: string;
  profile?: {
    firstName?: string;
    lastName?: string;
  };
}

interface Invitation {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  expiresAt: string;
  createdAt: string;
  acceptedAt?: string;
  invitedBy?: InvitedByUser;
  signupLink?: string; // Available for pending invitations
}

interface InvitationsViewerProps {
  onClose: () => void;
}

const InvitationsViewer: React.FC<InvitationsViewerProps> = ({ onClose }) => {
  const currentUser = useUser();
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'accepted' | 'expired'>('all');
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const isAdmin = currentUser?.role === 'admin';

  // ESC key to close modal
  useEscapeKey(true, onClose);

  useEffect(() => {
    loadInvitations();
  }, []);

  const loadInvitations = async () => {
    setLoading(true);
    try {
      const result = await getInvitations();
      if (result.success && result.data) {
        setInvitations(result.data);
      } else {
        toast.error(result.error || 'Failed to load invitations');
      }
    } catch (error) {
      toast.error('Failed to load invitations');
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (invitationId: string) => {
    if (!confirm('Are you sure you want to revoke this invitation? This cannot be undone.')) {
      return;
    }

    setRevokingId(invitationId);
    try {
      const result = await revokeInvitation(invitationId);
      if (result.success) {
        toast.success('Invitation revoked successfully');
        // Update local state
        setInvitations(prev => prev.map(inv => 
          inv._id === invitationId 
            ? { ...inv, status: 'revoked' as const }
            : inv
        ));
      } else {
        toast.error(result.error || 'Failed to revoke invitation');
      }
    } catch (error) {
      toast.error('Failed to revoke invitation');
    } finally {
      setRevokingId(null);
    }
  };

  const handleResend = async (invitationId: string) => {
    setResendingId(invitationId);
    try {
      const result = await resendInvitation(invitationId);
      if (result.success) {
        toast.success('Invitation email resent successfully');
        // Reload to get updated invitation data (new token/expiry for expired invites)
        await loadInvitations();
      } else {
        toast.error(result.error || 'Failed to resend invitation');
      }
    } catch (error) {
      toast.error('Failed to resend invitation');
    } finally {
      setResendingId(null);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'accepted':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'expired':
        return <AlertCircle className="h-4 w-4 text-gray-400" />;
      case 'revoked':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'expired':
        return 'bg-gray-100 text-gray-800';
      case 'revoked':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredInvitations = invitations.filter(inv => {
    if (filter !== 'all' && inv.status !== filter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const fullName = `${inv.firstName} ${inv.lastName}`.toLowerCase();
      return fullName.includes(q) || inv.email.toLowerCase().includes(q) || inv.role.toLowerCase().includes(q);
    }
    return true;
  });

  const formatDate = (dateString: string) => formatDateUtil(dateString);

  const isExpired = (expiresAt: string) => {
    return new Date(expiresAt) < new Date();
  };

  const copySignupLink = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Signup link copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">
              {isAdmin ? 'All Invitations' : 'Sent Invitations'}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {isAdmin 
                ? 'View all user invitations sent by admins and account managers' 
                : 'Track the status of your user invitations'
              }
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 px-6 pt-4 border-b border-gray-200">
          {(['all', 'pending', 'accepted', 'expired'] as const).map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                filter === status
                  ? 'bg-white text-blue-600 border-t-2 border-x border-gray-200 border-t-blue-600'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
              <span className="ml-2 text-xs bg-gray-100 px-2 py-0.5 rounded-full">
                {status === 'all' 
                  ? invitations.length 
                  : invitations.filter(inv => inv.status === status).length
                }
              </span>
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="px-6 pt-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, or role..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredInvitations.length === 0 ? (
            <div className="text-center py-12">
              <Mail className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">No invitations found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredInvitations.map((invitation) => (
                <div
                  key={invitation._id}
                  className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(invitation.status)}
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusColor(invitation.status)}`}>
                            {invitation.status}
                          </span>
                        </div>
                        <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800 font-medium">
                          {invitation.role}
                        </span>
                      </div>
                      
                      <h3 className="font-semibold text-gray-900">
                        {invitation.firstName} {invitation.lastName}
                      </h3>
                      <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                        <Mail className="h-3 w-3" />
                        {invitation.email}
                      </p>
                      
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-xs text-gray-500">
                        <span>Sent: {formatDate(invitation.createdAt)}</span>
                        {invitation.status === 'accepted' && invitation.acceptedAt && (
                          <span className="text-green-600">✓ Accepted: {formatDate(invitation.acceptedAt)}</span>
                        )}
                        {(invitation.status === 'pending' || invitation.status === 'expired') && (
                          <span className={isExpired(invitation.expiresAt) || invitation.status === 'expired' ? 'text-red-600' : ''}>
                            {isExpired(invitation.expiresAt) || invitation.status === 'expired' ? 'Expired' : 'Expires'}: {formatDate(invitation.expiresAt)}
                          </span>
                        )}
                        {/* Show who sent the invitation (for admins viewing all invitations) */}
                        {isAdmin && invitation.invitedBy && (
                          <span className="flex items-center gap-1 text-blue-600">
                            <User className="h-3 w-3" />
                            Invited by: {invitation.invitedBy.profile?.firstName} {invitation.invitedBy.profile?.lastName}
                            <span className="text-gray-400">
                              ({invitation.invitedBy.role === 'distributor' ? 'Account Manager' : invitation.invitedBy.role})
                            </span>
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Action Buttons - For pending and expired invitations */}
                    {(invitation.status === 'pending' || invitation.status === 'expired') && (
                      <div className="ml-4 flex items-center gap-2">
                        {/* Copy Link Button - Only for pending, non-expired invitations */}
                        {invitation.signupLink && invitation.status === 'pending' && !isExpired(invitation.expiresAt) && (
                          <button
                            onClick={() => copySignupLink(invitation.signupLink!)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
                            title="Copy signup link"
                          >
                            <Copy className="h-4 w-4" />
                            <span>Copy Link</span>
                          </button>
                        )}
                        {/* Resend Email Button */}
                        <button
                          onClick={() => handleResend(invitation._id)}
                          disabled={resendingId === invitation._id}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-green-600 hover:text-green-700 hover:bg-green-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title={isExpired(invitation.expiresAt) || invitation.status === 'expired' ? 'Resend with new link' : 'Resend invitation email'}
                        >
                          {resendingId === invitation._id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                          ) : (
                            <RefreshCw className="h-4 w-4" />
                          )}
                          <span>Resend</span>
                        </button>
                        {/* Revoke Button */}
                        <button
                          onClick={() => handleRevoke(invitation._id)}
                          disabled={revokingId === invitation._id}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Revoke invitation"
                        >
                          {revokingId === invitation._id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                          <span>Revoke</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="w-full sm:w-auto px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvitationsViewer;

